import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { I18nPipe } from '../../../../shared/pipes/i18n.pipe';

export type InscriptionStep3SubmitPayload = Record<string, never>;

@Component({
  selector: 'umkt-inscription-step3',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatRadioModule, MatCheckboxModule, I18nPipe],
  templateUrl: './inscription-step3.component.html',
  styleUrls: ['./inscription-step3.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InscriptionStep3Component {
  @Input({ required: true }) partnerForm!: FormGroup;
  @Input() isSubmitting = false;

  @Output() backStep = new EventEmitter<void>();
  @Output() submitStep = new EventEmitter<void>();

  readonly base = 'pages.partnerRegistration.inscription.step3.questions';

  readonly startupStageOptions = [
    'IDEACAO', 'VALIDACAO', 'CRESCIMENTO', 'TRACAO', 'ESCALA',
  ] as const;

  readonly businessTypeOptions = [
    'B2B', 'B2C', 'B2B2C', 'B2C2B', 'B2G', 'C2B', 'C2C',
  ] as const;

  readonly revenueModelOptions = [
    'SAAS', 'TRANSACIONAL', 'MARKETPLACE', 'LICENCIAMENTO', 'VENDA_DIRETA',
    'AFILIADOS', 'FREEMIUM', 'ANUNCIOS', 'OUTRO',
  ] as const;

  readonly mainSegmentOptions = [
    'AGRONEGOCIO', 'ALIMENTOS_BEBIDAS', 'AUTOMOTIVO_MOBILIDADE',
    'CAPITAL_INVESTIMENTOS', 'COMERCIO_ELETRONICO', 'COMUNICACAO_MIDIA',
    'CONSTRUCAO_IMOVEIS', 'CREDITO_FINANCAS', 'EDUCACAO', 'ENERGIA',
    'ESPORTES_LAZER', 'GESTAO_CONSULTORIA', 'GOVERNO_PODER_PUBLICO',
    'HOTELARIA_TURISMO', 'INDUSTRIA_TRANSFORMACAO', 'JOGOS_ENTRETENIMENTO',
    'LOGISTICA_TRANSPORTES', 'MODA_VESTUARIO', 'MOTORES_EQUIPAMENTOS',
    'OLEO_GAS', 'SAUDE_BEM_ESTAR', 'SEGURANCA_DEFESA', 'SEGUROS',
    'SERVICOS_PROFISSIONAIS', 'IMPACTO_SOCIOAMBIENTAL', 'TECNOLOGIA_INFORMACAO',
    'TELECOMUNICACOES', 'VAREJO_ATACADO',
  ] as const;

  readonly operationScaleOptions = [
    'BRASIL', 'AMERICA', 'GLOBAL',
  ] as const;

  isBusinessTypeSelected(opt: string): boolean {
    const value: string[] = this.partnerForm.get('businessType')?.value ?? [];
    return value.includes(opt);
  }

  toggleBusinessType(opt: string): void {
    const ctrl = this.partnerForm.get('businessType');
    if (!ctrl) return;
    const current: string[] = Array.isArray(ctrl.value) ? [...ctrl.value] : [];
    const index = current.indexOf(opt);
    if (index > -1) {
      current.splice(index, 1);
    } else {
      current.push(opt);
    }
    ctrl.setValue(current);
    ctrl.markAsTouched();
  }

  get isFormValid(): boolean {
    const keys = [
      'startupStage', 'revenueModel', 'mainSegment', 'operationScale',
    ];
    const baseValid = keys.every(k => {
      const ctrl = this.partnerForm.get(k);
      return ctrl?.valid && ctrl.value;
    });
    const businessTypeCtrl = this.partnerForm.get('businessType');
    const businessTypeValid = !!businessTypeCtrl?.valid;
    return baseValid && businessTypeValid;
  }

  onBack(): void {
    this.backStep.emit();
  }

  onSubmit(): void {
    const keys = [
      'startupStage', 'businessType', 'revenueModel',
      'mainSegment', 'operationScale',
    ];
    keys.forEach(k => this.partnerForm.get(k)?.markAsTouched());

    if (!this.isFormValid) return;
    this.submitStep.emit();
  }
}
